import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { createLink } from "../../utils";
import queryString from "query-string";
import Toolbar from "./Toolbar";
import { useGlobalValue } from "../../state/GlobalProvider";
import { SovosDataGridNext } from "mosaic-react";
import styles from "./DataGrid.module.sass";
import { parseFilter, parseSort, stringifySort } from "../../utils/queryUtils";
import { getCard, loadURLFilters, buildAvailableFilters, buildColumns, buildRows, applyFiltersToUrl, filterColumns, getColumnForNewFilter } from "./utils";
import DatagridFilters from "../DatagridFilters/DatagridFilters";
const DataGrid = props => {
  const {
    id,
    basePath,
    items: listItems,
    header,
    card,
    cardKey,
    className,
    metadata,
    page,
    pageSize,
    tabs,
    status = {
      loading: true
    },
    availableFilters,
    modifiers,
    sort,
    showDetailPanel,
    showCheckbox,
    extraActions,
    filter
  } = props;
  const {
    i18n,
    params,
    location,
    history,
    proxy,
    detailPanelComponents
  } = useGlobalValue();
  const queryParams = queryString.parse(location.search);
  const [filterModel, setFilterModel] = React.useState({
    items: loadURLFilters(location?.search),
    isLoaded: false
  });
  const filterArray = filter ? parseFilter(filter) : [];
  const [columns, setColumns] = useState([]);
  const [columnsToHide, setColumnsToHide] = useState({});
  const [sortModel, setSortModel] = useState(parseSort(sort));
  const [isPreferencesPanelOpen, setIsPreferencesPanelOpen] = useState(false);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: queryParams?.pageSize && parseInt(queryParams?.pageSize) || pageSize && parseInt(pageSize) || 20,
    page: queryParams?.page && parseInt(queryParams?.page) || page && parseInt(page) || 0
  });
  const rowCountRef = useRef(metadata?.total || 0);
  const availableFiltersRef = useRef(availableFilters || []);
  const headerRef = useRef(header || []);
  const Card = getCard(card, cardKey);
  const rows = buildRows(listItems, header);
  const getDetailPanelContent = useCallback(({
    row
  }) => {
    if (!row?.detailPanel || !row.detailPanel?.detailPanelComponent || !row.detailPanel?.data || !detailPanelComponents || !detailPanelComponents[row.detailPanel.detailPanelComponent]) {
      return null;
    }
    const DetailPanelComponent = detailPanelComponents[row.detailPanel.detailPanelComponent];
    return /*#__PURE__*/React.createElement(DetailPanelComponent, row);
  }, []);
  const isClickable = rows?.[0]?.link?.to;
  const modifiersClasses = (modifiers || []).map(m => styles[m]).filter(m => m).join(" ");
  const onPreferencePanelClose = params => {
    params.openedPanelValue === "columns" ? setIsPreferencesPanelOpen(false) : setIsFilterPanelOpen(false);
  };
  const onPreferencePanelOpen = params => {
    if (params.openedPanelValue === "columns") {
      setIsPreferencesPanelOpen(true);
      setIsFilterPanelOpen(false);
    } else {
      setIsFilterPanelOpen(true);
      setIsPreferencesPanelOpen(false);
    }
  };
  const rowCount = useMemo(() => {
    if (metadata?.total !== undefined) {
      rowCountRef.current = metadata.total;
    }
    return rowCountRef.current;
  }, [metadata?.total]);
  const availableFilterMemoized = useMemo(() => {
    if (!status?.loading && JSON.stringify(availableFilters || []) !== JSON.stringify(availableFiltersRef.current)) {
      availableFiltersRef.current = availableFilters;
    }
    return availableFiltersRef.current;
  }, [availableFilters]);
  const headerMemoized = useMemo(() => {
    if (header && JSON.stringify(header || []) !== JSON.stringify(headerRef.current)) {
      headerRef.current = header;
    }
    return headerRef.current;
  }, [header]);
  const onPaginationModelChange = paginationModel => {
    const queryParams = queryString.parse(location.search);
    setPaginationModel({
      page: paginationModel.page,
      pageSize: paginationModel.pageSize
    });
    history.replace({
      search: queryString.stringify({
        ...queryParams,
        page: paginationModel.page,
        pageSize: paginationModel.pageSize
      })
    });
  };
  const onSortModelChange = sortModel => {
    const queryParams = queryString.parse(location.search);
    if (!sortModel.length) {
      delete queryParams.sort;
    } else {
      queryParams.sort = stringifySort(sortModel.map(sort => {
        return {
          column: sort.field,
          order: sort.sort
        };
      }));
    }
    history.replace({
      search: queryString.stringify({
        ...queryParams
      })
    });
  };
  const onFilterChange = newFilter => {
    if (status?.loading || !filterModel.isLoaded) {
      return;
    }
    setFilterModel(prevState => ({
      ...prevState,
      items: newFilter?.items || []
    }));
  };
  const onRowClick = clickParams => {
    if (clickParams) {
      const path = createLink(basePath, proxy, clickParams.row, {
        ...params
      });
      path && history.push(path);
    }
  };
  useEffect(() => {
    const columnsObj = buildColumns(headerMemoized, Card, i18n);
    if (availableFilterMemoized?.length) {
      const {
        notVisibleColumns,
        columnsWithFilters
      } = buildAvailableFilters(availableFilterMemoized, columnsObj, i18n);
      setColumnsToHide(notVisibleColumns);
      setColumns(Object.values(columnsWithFilters || {}));
      setFilterModel(prevState => ({
        ...prevState,
        isLoaded: true
      }));
      return;
    }
    setColumns(Object.values(columnsObj || {}));
  }, [availableFilterMemoized, headerMemoized]);
  useEffect(() => {
    const debounceFilter = setTimeout(() => {
      if (status?.loading || !filterModel.isLoaded) {
        return;
      }
      applyFiltersToUrl(filterModel.items, availableFilterMemoized, history);
    }, 1000);
    return () => {
      clearTimeout(debounceFilter);
    };
  }, [JSON.stringify(filterModel.items)]);
  return /*#__PURE__*/React.createElement("div", {
    id: id,
    className: `${styles["root"]} ${className || ""} ${modifiersClasses || ""}`
  }, /*#__PURE__*/React.createElement(SovosDataGridNext, {
    sx: isClickable && {
      cursor: "pointer"
    },
    loading: status?.loading,
    filterModel: {
      items: filterModel.items
    },
    columnVisibilityModel: {
      ...columnsToHide
    },
    sortingMode: "server",
    sortModel: sortModel,
    onColumnVisibilityModelChange: newModel => setColumnsToHide(newModel),
    autoHeight: rows?.length > 0 && rows?.length < paginationModel.pageSize,
    disableColumnResize: false,
    columns: columns || [],
    rows: rows || [],
    paginationMode: "server",
    rowCount: rowCount,
    pageSizeOptions: rows?.length > 0 ? [20, 50, 100] : [],
    paginationModel: paginationModel,
    pagination: true,
    slots: {
      toolbar: Toolbar,
      filterPanel: DatagridFilters
    },
    slotProps: {
      toolbar: {
        tabs,
        isPreferencesPanelOpen,
        isFilterPanelOpen,
        extraActions,
        availableFilters: availableFilterMemoized,
        filterArray
      },
      panel: {
        placement: "bottom-end"
      },
      filterPanel: {
        availableFilters: availableFilterMemoized,
        filter,
        anchor: "results",
        filterFormProps: {
          filterColumns
        },
        getColumnForNewFilter
      }
    },
    filterMode: "server",
    onSortModelChange: onSortModelChange,
    onRowClick: onRowClick,
    onFilterModelChange: onFilterChange,
    onPreferencePanelOpen: onPreferencePanelOpen,
    onPreferencePanelClose: onPreferencePanelClose,
    onPaginationModelChange: onPaginationModelChange,
    getDetailPanelContent: showDetailPanel ? getDetailPanelContent : null,
    getDetailPanelHeight: () => "auto",
    checkboxSelection: showCheckbox,
    disableColumnMenu: false
  }));
};
export default DataGrid;